import React, { useRef, useState } from 'react';
import TableHeader from '../../../BaseComponents/TableHeader';
import TableSkeleton from '../../../BaseComponents/TableSkeleton';
import './SupplementList.scss';
import SupplementTableRow from '../SupplementTableRow/SupplementTableRow';
import { Supplement, getSupplementsList } from '../../../../api/supplements';

const headerColumns = [
  { title: 'Image', key: 'image', minWidth: '80px', flex: 0, sortable: false },
  { title: 'Supplement Name', key: 'name', minWidth: '200px', flex: 0, sortable: true, apiKey: 'supplement_name' },
  { title: 'Description', key: 'description', minWidth: '300px', flex: 1, sortable: true, apiKey: 'description' },
];

interface SupplementListProps {
  supplements: Supplement[];
  isLoading: boolean;
  onEdit: (supplement: Supplement) => void;
  lastSupplementElementRef: (node: HTMLDivElement) => void;
  isLoadingMore: boolean;
  onSupplementsUpdate?: (supplements: Supplement[]) => void;
}

const SupplementList: React.FC<SupplementListProps> = ({ 
  supplements, 
  isLoading, 
  onEdit,
  lastSupplementElementRef,
  isLoadingMore,
  onSupplementsUpdate
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [sortSupplements, setSortSupplements] = useState<Supplement[]>([]);
  const [sortingLoading, setSortingLoading] = useState<boolean>(false);
  
  const fetchSortedSupplements = async (columnKey: string, direction: 'asc' | 'desc') => {
    try {
      let sortBy = '';
      if (columnKey === 'name') {
        sortBy = 'supplement_name';
      } else if (columnKey === 'description') {
        sortBy = 'description';
      }
      
      const response = await getSupplementsList('', sortBy, direction, 1000, 0);
      return response.supplements || [];
    } catch (error) {
      console.error('Error fetching sorted supplements:', error);
      return [...supplements].sort((a, b) => {
        let aValue: any, bValue: any;

        if (columnKey === 'name') {
          aValue = a.supplement_name.toLowerCase();
          bValue = b.supplement_name.toLowerCase();
        } else if (columnKey === 'description') {
          aValue = a.description.toLowerCase();
          bValue = b.description.toLowerCase();
        } else {
          return 0;
        }

        if (direction === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }
  };
  
  const onSort = async (columnKey: string, direction: 'asc' | 'desc') => {
    try {
      setSortingLoading(true);
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const sortedSupplements = await fetchSortedSupplements(columnKey, direction);
      setSortSupplements(sortedSupplements);
      
      if (onSupplementsUpdate) {
        onSupplementsUpdate(sortedSupplements);
      }
    } catch (err) {
      console.error('Error sorting supplements:', err);
    } finally {
      setSortingLoading(false);
    }
  };

  const displaySupplements = sortSupplements.length > 0 ? sortSupplements : supplements;

  if (isLoading) {
    return (
      <div className="loading-container">
        Loading supplements...
      </div>
    );
  }

  return (
    <div className='scroll-container'>
      <div className='table-container supplements-table'>
        <TableHeader headerColumns={headerColumns} onSort={onSort} />
        <div className='table-body-supplement' ref={scrollContainerRef}>
          {sortingLoading ? (
            <TableSkeleton rows={5} columns={3} />
          ) : displaySupplements?.length > 0 ? (
            displaySupplements.map((supplement, index) => {
              const isLastElement = index === displaySupplements.length - 1;
              return (
                <div
                  key={supplement.id || supplement.supplement_id || `supplement-${index}`}
                  ref={isLastElement ? lastSupplementElementRef : undefined}
                >
                  <SupplementTableRow
                    supplementData={supplement}
                    onEdit={() => onEdit(supplement)}
                  />
                </div>
              );
            })
          ) : (
            <div className="no-data">
              No supplements available.
            </div>
          )}
          {isLoadingMore && (
            <div className="loading-more">
              <div className="loading-spinner"></div>
              <span>Loading more supplements...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplementList;
